import React from "react";
import "./ResultDashboard.css";
import { formatEuro } from "../utils/format";

export default function ResultDashboard({ data }) {
  if (!data || !data.kaufpreis) return null;

  // --- Eingaben sicher in Zahlen wandeln ---
  const income = Number(data.income) || 0;
  const taxClass = data.taxClass;
  const churchTax = data.churchTax;

  const wohnflaeche = Number(String(data.wohnflaeche).replace(",", ".")) || 0;
  const mietpreisProQm =
    Number(String(data.mietpreisProQm).replace(",", ".")) || 0;
  const garageMiete = Number(String(data.garageMiete).replace(",", ".")) || 0;

  const zinssatz = Number(String(data.zinssatz).replace(",", ".")) || 0; // %
  const tilgungssatz = Number(String(data.tilgungssatz).replace(",", ".")) || 1; // %
  const kaufpreis =
    Number(String(data.kaufpreis).replace(/\./g, "").replace(",", ".")) || 0;
  const bodenAnteil = Number(String(data.bodenAnteil).replace(",", ".")) || 0;

  // --- 1) Nettokaltmiete (ohne Küchenkosten, die sind einmalig) ---
  const kaltmiete = wohnflaeche * mietpreisProQm;
  const gesamtnettomiete = kaltmiete + garageMiete; // €/Monat
  const jahresmiete = gesamtnettomiete * 12;

  // --- 2) Gebäudewert (AfA-Basis) ---
  const gebaeudewert = kaufpreis * ((100 - bodenAnteil) / 100);

  // --- 3) Gesamtkosten inkl. 7% NK -> Finanzierungsbasis / Darlehen ---
  const gesamtkosten = kaufpreis * 1.07;
  const darlehensbetrag = gesamtkosten;

  // --- 4) Annuität (Zins + Tilgung) auf Darlehen ---
  const zinssatzDezimal = zinssatz / 100;
  const tilgungssatzDezimal = tilgungssatz / 100;
  const annuitaetProJahr =
    darlehensbetrag * (zinssatzDezimal + tilgungssatzDezimal);
  const annuitaetProMonat = annuitaetProJahr / 12;

  // --- 5) AfA (5% auf Gebäudewert) ---
  const absetzungProJahr = gebaeudewert * 0.05;

  // --- 6) Effektiver Steuersatz (vereinfacht) ---
  let steuersatz =
    income > 100000
      ? 0.42
      : income > 60000
      ? 0.35
      : income > 30000
      ? 0.25
      : 0.2;

  switch (taxClass) {
    case "II":
      steuersatz -= 0.02;
      break;
    case "III":
      steuersatz -= 0.06;
      break;
    case "V":
      steuersatz += 0.06;
      break;
    case "VI":
      steuersatz += 0.1;
      break;
    default:
      break; // I & IV unverändert
  }
  if (churchTax === "ja") steuersatz *= 1.08;
  // Sicherheitskorridor
  steuersatz = Math.max(0, Math.min(0.5, steuersatz));

  // --- 6b) Steuerliche Bemessungsgrundlage (Vermietung & Verpachtung) ---
  // Einnahmen - (nur) Zinskosten - AfA
  const jahreszins = darlehensbetrag * zinssatzDezimal; // nur Zinsen absetzbar
  const steuerlicheBemessung = jahresmiete - jahreszins - absetzungProJahr;

  // Steuer-Wirkung (positiv = Steuerlast, negativ = Steuergutschrift)
  const steuerWirkungJahr = steuersatz * steuerlicheBemessung;
  const steuerWirkungMonat = steuerWirkungJahr / 12;

  // --- 7) Effektiver Zuschussbedarf (nach Steuer) ---
  // Annuität - Miete + Steuerwirkung
  const monatlicheBelastung =
    annuitaetProMonat - gesamtnettomiete + steuerWirkungMonat;

  // Für Anzeige
  const formatPercent = (v) => `${(v * 100).toFixed(1).replace(".", ",")} %`;
  const istUeberschuss = monatlicheBelastung < 0;
  const labelCashflow = istUeberschuss
    ? "Cashflow-Überschuss"
    : "Effektiver Zuschussbedarf";
  const betragCashflow = formatEuro(Math.abs(monatlicheBelastung)) + " / Monat";

  return (
    <div className="result-dashboard">
      <h2>📊 Ergebnisübersicht</h2>
      <p
        style={{ marginTop: "-0.5rem", marginBottom: "1.5rem", color: "#666" }}
      >
        Steuerklasse: <strong>{taxClass}</strong> · effektiver Steuersatz:{" "}
        <strong>{formatPercent(steuersatz)}</strong>
      </p>

      <div className="result-grid">
        <div>
          <strong>Gesamtkosten inkl. Kaufnebenkosten (7 %):</strong>
        </div>
        <div>{formatEuro(gesamtkosten)}</div>

        <div>
          <strong>Nettokaltmiete gesamt:</strong>
        </div>
        <div>{formatEuro(gesamtnettomiete)} / Monat</div>

        <div>
          <strong>Annuität (Zins + Tilgung) auf Darlehen:</strong>
        </div>
        <div>{formatEuro(annuitaetProMonat)} / Monat</div>

        <div>
          <strong>Abschreibung (AfA 5 % auf Gebäudewert):</strong>
        </div>
        <div>{formatEuro(absetzungProJahr)} / Jahr</div>

        <div>
          <strong>Steuerwirkung (geschätzt):</strong>
        </div>
        <div>{formatEuro(steuerWirkungMonat)} / Monat</div>

        <div>
          <strong>{labelCashflow}:</strong>
        </div>
        <div>
          <strong>{betragCashflow}</strong>
        </div>
      </div>

      <p className="disclaimer">
        Annahmen: Annuität auf Gesamtkosten (inkl. 7 % NK), AfA nur auf
        Gebäudewert (Bodenanteil {String(bodenAnteil).replace(".", ",")} %).
        Werte gerundet und vereinfacht.
      </p>
    </div>
  );
}
